import type { AnalyticsEventPayload } from '@atlaskit/analytics-next';
import type { Metric } from '@atlassian/browser-metrics/types';
import {
	isConversationChannelDataMessage,
	isFollowUpMessage,
	isStreamActionClassification,
	isStreamActionComplete,
	isStreamActionError,
	isStreamActionPart,
	isStreamActionPrepare,
	isStreamAnswerGenerationType,
	isStreamAnswerPart,
	isStreamDynamicLayoutSubAgent,
	isStreamError,
	isStreamHeartbeat,
	isStreamReasoningPart,
	isStreamResearchPayload,
	isStreamResearchPlan,
	isStreamResponse,
	isStreamTrace,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { deadAirMetrics } from '../../common/utils/browser-metrics';

type DeadAirTrackerConfig = {
	messageId: string;
	conversationId: string;
	traceId: string;
	productKey: string;
	deepResearchEnabled: boolean;
	thinkDeeperEnabled: boolean;
	fastModeEnabled: boolean;
	webSearchEnabled: boolean;
	isAIFC: boolean;
	sendAnalyticsEvent: (payload: AnalyticsEventPayload) => void;
};

/**
 * Creates a dead air tracker that monitors gaps between meaningful stream messages.
 * Dead air represents periods when users don't see any UI updates during streaming responses.
 */
export const createDeadAirTracker = (config: DeadAirTrackerConfig) => {
	let previousMessageTimestamp: number = performance.now();
	let previousMessageTemplate: string | undefined;
	let previousMessageType: string | undefined;
	let maxDeadAirMs = 0;
	let maxPreviousMessageTemplate: string | undefined;
	let maxCurrentMessageTemplate: string | undefined;
	let maxPreviousMessageType: string | undefined;
	let maxCurrentMessageType: string | undefined;

	const shouldIgnoreMessage = (message: StreamMessage): boolean => {
		return (
			isConversationChannelDataMessage(message) ||
			isStreamHeartbeat(message) ||
			isStreamDynamicLayoutSubAgent(message) ||
			isStreamAnswerGenerationType(message)
		);
	};

	const shouldCountMessage = (message: StreamMessage): boolean => {
		return (
			isStreamResearchPayload(message) ||
			isStreamResearchPlan(message) ||
			isStreamTrace(message) ||
			isStreamAnswerPart(message) ||
			isStreamReasoningPart(message) ||
			isStreamActionPart(message) ||
			isStreamResponse(message) ||
			isFollowUpMessage(message) ||
			isStreamActionClassification(message) ||
			isStreamActionPrepare(message) ||
			isStreamActionComplete(message) ||
			isStreamActionError(message) ||
			isStreamError(message)
		);
	};

	const getMessageTemplate = (message: StreamMessage): string | undefined => {
		return 'message' in message && message.message?.message_template;
	};

	const commonMetadata = {
		messageId: config.messageId,
		conversationId: config.conversationId,
		traceId: config.traceId,
		productKey: config.productKey,
		isExplicitDeepResearchEnabled: config.deepResearchEnabled,
		isExplicitThinkDeeperEnabled: config.thinkDeeperEnabled,
		isExplicitFastModeEnabled: config.fastModeEnabled,
		isWebSearchEnabled: config.webSearchEnabled,
		isAIFC: config.isAIFC,
		isExplicitThinkFasterEnabled: false,
		isImplicitThinkDeeper: false,
		hasSwitchedToFastThinking: false,
	};

	const trackDeadAir = (
		deadAirMs: number,
		previousTemplate: string | undefined,
		currentTemplate: string | undefined,
		previousType: string | undefined,
		currentType: string | undefined,
	) => {
		const intervalMetric = deadAirMetrics.createDeadAirIntervalMetrics();
		const now = performance.now();

		intervalMetric.start({ startTime: now - deadAirMs });
		intervalMetric.stop({
			stopTime: now,
			customData: {
				...commonMetadata,
				previousMessageTemplate: previousTemplate ?? '',
				messageTemplate: currentTemplate ?? '',
				previousMessageType: previousType ?? '',
				messageType: currentType ?? '',
			},
		});

		if (deadAirMs > maxDeadAirMs) {
			maxDeadAirMs = deadAirMs;
			maxPreviousMessageTemplate = previousTemplate;
			maxCurrentMessageTemplate = currentTemplate;
			maxPreviousMessageType = previousType;
			maxCurrentMessageType = currentType;
		}
	};

	const update = (streamMessage: StreamMessage): void => {
		if (shouldIgnoreMessage(streamMessage)) {
			return;
		}

		if (!shouldCountMessage(streamMessage)) {
			config.sendAnalyticsEvent({
				eventType: 'operational',
				action: 'unrecognized',
				actionSubject: 'deadAirTrackerMessage',
				attributes: {
					messageType: streamMessage.type ?? 'unknown',
					messageTemplate: getMessageTemplate(streamMessage) ?? 'unknown',
					conversationId: config.conversationId,
					traceId: config.traceId,
				},
			});
			return;
		}

		const currentTimestamp = performance.now();
		const currentTemplate = getMessageTemplate(streamMessage);
		const currentMessageType = streamMessage.type;

		// Skip consecutive streaming messages (continuous token/content generation)
		// ANSWER_PART: Standard LLM response tokens
		// REASONING_PART: Think deeper extended reasoning phases (streamed thinking)
		// ACTION_PART: AIFC content generation chunks (whiteboards, documents, etc.)
		const isConsecutiveAnswerPart =
			previousMessageType === 'ANSWER_PART' && currentMessageType === 'ANSWER_PART';
		const isConsecutiveReasoningPart =
			previousMessageType === 'REASONING_PART' && currentMessageType === 'REASONING_PART';
		const isConsecutiveActionPart =
			previousMessageType === 'ACTION_PART' && currentMessageType === 'ACTION_PART';

		if (isConsecutiveAnswerPart || isConsecutiveReasoningPart || isConsecutiveActionPart) {
			previousMessageTimestamp = currentTimestamp;
			previousMessageTemplate = currentTemplate;
			previousMessageType = currentMessageType;
			return;
		}

		const deadAirMs = currentTimestamp - previousMessageTimestamp;
		trackDeadAir(
			deadAirMs,
			previousMessageTemplate,
			currentTemplate,
			previousMessageType,
			currentMessageType,
		);

		previousMessageTimestamp = currentTimestamp;
		previousMessageTemplate = currentTemplate;
		previousMessageType = currentMessageType;
	};

	const emitMaxDeadAir = (): void => {
		if (maxDeadAirMs === 0) {
			return;
		}

		const maxDeadAirMetric: Metric = deadAirMetrics.createDeadAirMaxMetrics();
		const now = performance.now();

		maxDeadAirMetric.start({ startTime: now - maxDeadAirMs });
		maxDeadAirMetric.stop({
			stopTime: now,
			customData: {
				...commonMetadata,
				maxDeadAirMs,
				previousMessageTemplate: maxPreviousMessageTemplate ?? '',
				messageTemplate: maxCurrentMessageTemplate ?? '',
				previousMessageType: maxPreviousMessageType ?? '',
				messageType: maxCurrentMessageType ?? '',
			},
		});
	};

	const complete = (): void => {
		emitMaxDeadAir();
	};

	const cancel = (): void => {
		emitMaxDeadAir();
	};

	return {
		update,
		complete,
		cancel,
	};
};
